<script setup lang="ts">
import type { Hashtag } from '@store'

defineOptions({
  inheritAttrs: false,
})

const postStore = usePostStore()
const etcStore = useEtcStore()
const { hide: modalHide } = useModal()
const show = ref(false)
const hashtagKeyword = ref('')
const filteredHashtags = computed(() =>
  etcStore.hashtagRankingTertiary.filter((hashtag) =>
    hashtag.hashtagNm.includes(hashtagKeyword.value)
  )
)

// 팝업 열기
const handleOnOpen = () => {
  window.addEventListener('resize', handleOnWindowResize)
}

// 팝업 닫기
const handleOnClose = () => {
  modalHide(modalsName.MODAL_ALL_HASHTAGS)
}

// 팝업 닫은 후
const handleOnClosed = () => {
  window.removeEventListener('resize', handleOnWindowResize)
}

// 해시태그 변경
const handleOnChangeActiveHashtag = (hashtag: Hashtag) => {
  postStore.updateHashtagControl(hashtag)
  handleOnClose()
}

// 모바일 사이즈 이상 윈도우 사이즈가 늘어나면
const handleOnWindowResize = () => {
  if (window.innerWidth > 768) {
    handleOnClose()
  }
}
</script>

<template>
  <VueFinalModal
    v-model="show"
    :name="modalsName.MODAL_ALL_HASHTAGS"
    :content-class="'modal-content'"
    classes="modal-container"
    @opened="handleOnOpen"
    @closed="handleOnClosed"
    @click-outside="handleOnClose"
  >
    <ClientOnly>
      <NovaBoxBase v-if="show" class="modal-box">
        <div class="modal-header">
          <h4 class="title">#{{ $t('hashtag') }}</h4>

          <NovaButtonIcon
            :icon="{ type: 'outline', name: 'close-extend' }"
            :theme="'transparent'"
            :size="20"
            class="btn-close"
            @click="handleOnClose"
          />
        </div>

        <div class="modal-body">
          <div class="search-wrap">
            <NovaInput
              v-model="hashtagKeyword"
              :placeholder="$t('searchHashtag')"
              :theme="'search'"
            >
              <template #tools>
                <NovaButtonIcon
                  :icon="{ type: 'outline', name: 'search' }"
                  class="search-button"
                />
              </template>
            </NovaInput>
          </div>

          <NovaLayoutScrollContainer class="hashtags-wrap">
            <NovaHashtags
              v-if="filteredHashtags.length"
              :hashtags="filteredHashtags"
              :prefix="'#'"
              :able-action="true"
              :select-hashtag="postStore.hashtagControl.hashtag"
              @on-change-active-hashtag="handleOnChangeActiveHashtag"
            />

            <NovaBoxEmptyMessage
              v-else
              :message="$t('statusMessages.hashtagRanking.empty')"
              :fill="true"
            />
          </NovaLayoutScrollContainer>
        </div>
      </NovaBoxBase>
    </ClientOnly>
  </VueFinalModal>
</template>

<style lang="scss" scoped>
:deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.vfm--overlay) {
  background-color: hex-to-rgba($color-black, 0.65);
}

:deep(.modal-content) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  pointer-events: none;

  .modal-box {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    padding: 0;
    pointer-events: auto;
    border-radius: 0 !important;

    .modal-header {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 56px;
      padding: 0 20px;
      color: $color-text-2;
      background-color: $color-bg-3;
      border-bottom: 1px solid $border-1;

      .title {
        @include text-style($text-display-bold);
      }
    }

    .modal-body {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;

      .search-wrap {
        flex-shrink: 0;
        padding: 25px 10px;
      }

      .hashtags-wrap {
        flex-grow: 1;
        padding: 0 10px 25px;
        height: 100%;
      }
    }
  }
}
</style>
